/* eslint-disable react/no-unknown-property */
import React, { useState } from 'react'
import { Text, Checkbox, Button } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { LoadingDots } from '../../components/index.js'

export interface SubscribeProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  subscribeMetadata: any
  onSubmit?: ({
    email,
    terms,
    news,
    dealsType,
    onSuccess,
  }: {
    email: string
    terms: string
    news: string
    dealsType?: string
    onSuccess?: () => void
  }) => void
  loading?: boolean
}

/**
 * Subscribe 订阅模块
 * @param subscribeMetadata 订阅模块数据
 * @param onSubmit 提交事件
 * @param className 类名
 * @returns
 */
const Subscribe = ({ subscribeMetadata, onSubmit, className, loading, ...rest }: SubscribeProps) => {
  const [error, setError] = useState<string | null>(null)
  const [termsChecked, setTermsChecked] = useState<boolean>(subscribeMetadata?.termsChecked)
  const [newsChecked, setNewsChecked] = useState<boolean>(subscribeMetadata?.newsChecked)
  const [termsError, setTermsError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.target as HTMLFormElement)
    const email = formData.get('email')
    const terms = formData.get('terms')
    const news = formData.get('news')
    if (!email) {
      setError(subscribeMetadata?.emptyError)
      return
    }
    if (!/^[a-zA-Z0-9.,%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email as string)) {
      setError(subscribeMetadata?.formatError)
      return
    }
    if (!terms) {
      setTermsError(subscribeMetadata?.termsError)
      return
    }
    onSubmit?.({
      email: email as string,
      terms: terms as string,
      news: news as string,
      dealsType: subscribeMetadata?.dealsType,
      onSuccess: () => {
        setSuccess(true)
        let timer = setTimeout(() => {
          setSuccess(false)
          clearTimeout(timer)
        }, 3000)
      },
    })
  }

  return (
    <div
      {...rest}
      className={cn(
        'subscribe-component laptop:flex-row laptop:gap-4 desktop:flex-col desktop:gap-8 flex flex-col gap-4',
        className
      )}
    >
      {/* TODO: @seasail 用tailwind 原子类或者行内样式(Inline Style)代替 jsx global */}
      <style jsx global>
        {`
          .subscribe-component input:-webkit-autofill,
          .subscribe-component input:-webkit-autofill:hover,
          .subscribe-component input:-webkit-autofill:focus {
            -webkit-box-shadow: 0 0 0 1000px transparent inset !important; /* 背景透明 */
            -webkit-text-fill-color: #fff !important;
            transition: background-color 9999s ease-in-out 0s !important;
            caret-color: #fff;
          }
        `}
      </style>
      <div className="laptop:flex-1">
        <Text
          as="p"
          html={subscribeMetadata?.title}
          className="desktop:text-base text-sm font-bold leading-[1.4] text-white"
        />
        <Text
          as="p"
          html={subscribeMetadata?.subtitle}
          className="desktop:mt-2 desktop:text-[32px] mt-1 text-2xl font-bold leading-[1.2] text-white"
        />
      </div>
      <form className="laptop:flex-1" onSubmit={handleSubmit}>
        <div className="desktop:h-[48px] flex h-[38px] items-center">
          <div className="tablet:max-w-[320px] laptop:max-w-[404px] relative h-full flex-1">
            <input
              name="email"
              onFocus={() => setError(null)}
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={subscribeMetadata?.placeholder}
              className="desktop:text-base rounded-l-btn size-full rounded-r-none border border-[#B6B6BA] bg-transparent px-4 text-sm font-bold leading-[1.2] text-white"
            />
            <button
              onClick={() => setEmail('')}
              className={cn('absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600', {
                hidden: !email,
              })}
            >
              ✕
            </button>
          </div>
          <Button
            type="submit"
            className="desktop:px-7 rounded-r-btn desktop:text-base h-full !rounded-l-none bg-white px-5 text-sm font-bold leading-[1.2] text-black"
          >
            {loading ? (
              <LoadingDots />
            ) : (
              <Text html={subscribeMetadata?.button} className="desktop:text-base text-sm font-bold leading-[1.2]" />
            )}
          </Button>
        </div>
        {error && (
          <Text as="p" html={error} className="laptop:text-base mt-2 text-sm font-bold leading-[1.2] text-[#F84D4F]" />
        )}
        <div className="mt-4 flex flex-col gap-2">
          <div className="laptop:items-center flex items-start gap-2">
            <Checkbox
              name="terms"
              id="terms"
              size="base"
              checked={termsChecked}
              onCheckedChange={(checked: boolean) => {
                setTermsChecked(checked)
                checked && setTermsError(null)
              }}
              className="border-[#B6B6BA] data-[state=checked]:bg-white data-[state=checked]:text-[#080A0F]"
            />
            <label htmlFor="terms">
              <Text
                as="p"
                html={subscribeMetadata?.terms}
                className="desktop:text-base text-sm font-bold leading-[1.4] text-[#B6B6BA]"
              />
            </label>
          </div>
          {termsError && (
            <Text
              as="p"
              html={termsError}
              className="laptop:text-base text-sm font-bold leading-[1.2] text-[#F84D4F]"
            />
          )}
          <div className="flex items-start gap-2">
            <Checkbox
              name="news"
              id="news"
              size="base"
              checked={newsChecked}
              onCheckedChange={(checked: boolean) => {
                setNewsChecked(checked)
              }}
              className="border-[#B6B6BA] data-[state=checked]:bg-white data-[state=checked]:text-[#080A0F]"
            />
            <label htmlFor="news">
              <Text
                as="p"
                html={subscribeMetadata?.news}
                className="desktop:text-base text-sm font-bold leading-[1.4] text-[#B6B6BA]"
              />
            </label>
          </div>
          {success && (
            <Text
              as="p"
              html={subscribeMetadata?.successMessage}
              className="text-brand-0 text-base font-bold leading-[1.2]"
            />
          )}
        </div>
      </form>
    </div>
  )
}

export default Subscribe
